import { useMemo } from 'react';
import { cn } from './utils/cn';

export interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** CSS color (eg 'rgb(var(--color-brand-500))'). Defaults to current color. */
  stroke?: string;
  /** Optional fill color for the area below the line. */
  fill?: string;
  className?: string;
  showLastDot?: boolean;
}

/**
 * Inline SVG sparkline with optional area fill.
 * Renders as a 1-line stat decoration; not interactive.
 * Direction-agnostic: when used in RTL, callers usually want to pass
 * their `values` reversed so the most recent value is on the start side.
 */
export function Sparkline({
  values,
  width = 120,
  height = 36,
  stroke,
  fill,
  className,
  showLastDot = true,
}: SparklineProps) {
  const path = useMemo(() => {
    if (values.length === 0) return { line: '', area: '', last: null as null | { x: number; y: number } };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = values.length > 1 ? width / (values.length - 1) : 0;
    const points = values.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return [x, y] as const;
    });
    const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
    const area = `${line} L${width.toFixed(2)},${height.toFixed(2)} L0,${height.toFixed(2)} Z`;
    const last = points[points.length - 1];
    return { line, area, last: last ? { x: last[0], y: last[1] } : null };
  }, [values, width, height]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={cn('block overflow-visible', className)}
      role="img"
      aria-hidden
    >
      {fill && path.area ? <path d={path.area} fill={fill} /> : null}
      {path.line ? (
        <path
          d={path.line}
          fill="none"
          stroke={stroke ?? 'currentColor'}
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
      {showLastDot && path.last ? (
        <circle cx={path.last.x} cy={path.last.y} r={2.4} fill={stroke ?? 'currentColor'} />
      ) : null}
    </svg>
  );
}

export interface BarChartProps {
  data: { label: string; value: number; fill?: string }[];
  format?: (n: number) => string;
  max?: number;
  className?: string;
}

/**
 * Horizontal bars used for top-N spend / category breakdowns.
 * Single-row per data point; dense and RTL-safe (logical paddings).
 */
export function BarChart({ data, format, max, className }: BarChartProps) {
  const cap = max ?? Math.max(1, ...data.map((d) => d.value));
  return (
    <ul className={cn('space-y-2', className)}>
      {data.map((d) => {
        const pct = Math.max(2, Math.round((d.value / cap) * 100));
        return (
          <li key={d.label}>
            <div className="flex items-center justify-between text-xs text-fg-muted mb-1">
              <span className="truncate">{d.label}</span>
              <span className="num">{format ? format(d.value) : d.value}</span>
            </div>
            <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${pct}%`, background: d.fill ?? 'rgb(var(--color-brand-500))' }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
